import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from 'src/Components/Button/Button';
import closeIcon from 'src/images/close-icon.svg';
import { DeleteIcon, EditIcon, ZoomInIcon, ZoomOutIcon } from 'src/SharedComponents/Icons/Icons';
import { logger } from 'src/Utils/LoggerService/LoggerService';
import './LogoPreviewDialog.css';

interface IDragState {
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    frameWidth: number;
    frameHeight: number;
}

interface ILogoPreviewDialogParams {
    /** The image the user picked, shown live in the preview. */
    file: File;
    /** Called with the cropped logo once the user saves. */
    onSave: (logo: File) => void;
    onClose: () => void;
    /** Pick a different file (the pencil). */
    onReplace: () => void;
    /** Discard / remove the logo (the bin). */
    onDelete: () => void;
}

// Aspect of the Figma preview frame (512 x 258). The logo is cropped to
// exactly this shape, so the frame, both destination previews and the
// exported file all share it.
const FRAME_ASPECT = 512 / 258;

// Zoom frames the logo inside the fixed output box. At 1x the whole logo is
// visible, letterboxed, and nothing is cut off — the design's default, sitting
// at the middle of the slider. Below 1x the logo sits smaller with transparent
// margin around it; above 1x the box closes in and crops.
const DEFAULT_ZOOM = 1;
const MIN_ZOOM = 1 / 3;
const MAX_ZOOM = 3;

/* The slider carries a 0..1 position, not the zoom itself, and maps to zoom
   geometrically: equal travel is an equal ratio, which is how zoom is actually
   perceived. MIN_ZOOM * MAX_ZOOM === 1, so the midpoint lands exactly on 1x —
   the default handle position in the design. */
const ZOOM_RANGE = Math.log(MAX_ZOOM / MIN_ZOOM);
// MIN_ZOOM * MAX_ZOOM === 1 by construction, so the default is the exact
// midpoint. Stated directly, because log/exp leaves it a hair under 0.5.
const DEFAULT_SLIDER_POSITION = 0.5;
const zoomToSlider = (zoom: number) =>
    (zoom === DEFAULT_ZOOM ? DEFAULT_SLIDER_POSITION : Math.log(zoom / MIN_ZOOM) / ZOOM_RANGE);
const sliderToZoom = (position: number) => MIN_ZOOM * Math.exp(position * ZOOM_RANGE);

// Exp/log round-trips land a hair off 1, which would defeat the byte-for-byte
// upload at the default. Snapping also makes "fit" easy to come back to.
const ZOOM_SNAP = 0.02;

// Width of the exported crop; the height follows FRAME_ASPECT.
const OUTPUT_WIDTH = 1024;

// Quality for the JPEG path below. High enough that a re-encode of an already
// lossy source is not visibly worse.
const JPEG_QUALITY = 0.92;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Size of the logo as a percentage of the frame: contain-fit first, then
 * scaled by the zoom. Percentages (rather than pixels) mean the same pair of
 * numbers drives the large preview and both small destination tiles, so all
 * three show the identical crop without measuring any of them.
 */
const getFramePercentages = (imageAspect: number, zoom: number) => {
    const widthIsLimit = imageAspect > FRAME_ASPECT;
    return {
        widthPct: (widthIsLimit ? 1 : imageAspect / FRAME_ASPECT) * zoom * 100,
        heightPct: (widthIsLimit ? FRAME_ASPECT / imageAspect : 1) * zoom * 100,
    };
};

const LogoPreviewDialog: React.FC<ILogoPreviewDialogParams> = ({ file, onSave, onClose, onReplace, onDelete }) => {
    const { t } = useTranslation();

    const imageRef = useRef<HTMLImageElement | null>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    // canvas.toBlob hands its callback to the browser, which will run it
    // whether or not this dialog still exists. onSave lives on the parent, so
    // a late callback would upload a logo the user had already dismissed.
    const isLiveRef = useRef(true);
    // One Save is one upload: a second click before the encode finishes would
    // otherwise queue a second PUT.
    const saveInFlightRef = useRef(false);
    // Live drag state lives in a ref so the pointer handlers don't re-bind on
    // every move.
    const dragRef = useRef<IDragState | null>(null);

    const [imageSrc, setImageSrc] = useState('');
    const [imageAspect, setImageAspect] = useState<number | null>(null);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    // Distance the logo is nudged from centre, as a percentage of the frame —
    // the same unit as the size below, so the small previews stay in step.
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const url = URL.createObjectURL(file);
        setImageSrc(url);
        setZoom(DEFAULT_ZOOM);
        setOffset({ x: 0, y: 0 });
        setImageAspect(null);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    useEffect(() => () => { isLiveRef.current = false; }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        // An SVG without intrinsic dimensions reports a natural size of 0, in
        // which case the laid-out size is the only thing available.
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        setImageAspect(width > 0 && height > 0 ? width / height : FRAME_ASPECT);
    }, []);

    const geometryFor = useCallback(
        (value: number) => getFramePercentages(imageAspect ?? FRAME_ASPECT, value),
        [imageAspect],
    );

    // The logo can only be moved along an axis on which it overflows the
    // frame: at zoom 1 it is letterboxed, so there is nothing to pan to.
    const panLimitFor = useCallback((value: number) => {
        const { widthPct, heightPct } = geometryFor(value);
        return { x: Math.max(0, (widthPct - 100) / 2), y: Math.max(0, (heightPct - 100) / 2) };
    }, [geometryFor]);

    const clampOffset = useCallback((next: { x: number; y: number }, value: number) => {
        const limit = panLimitFor(value);
        return { x: clamp(next.x, -limit.x, limit.x), y: clamp(next.y, -limit.y, limit.y) };
    }, [panLimitFor]);

    const applyZoom = useCallback((next: number) => {
        const clamped = clamp(next, MIN_ZOOM, MAX_ZOOM);
        const value = Math.abs(clamped - DEFAULT_ZOOM) < ZOOM_SNAP ? DEFAULT_ZOOM : clamped;
        // Scale the offset with the zoom so whatever the user has centred
        // stays centred, then pull it back inside the new bounds — zooming
        // out has to reel a panned logo back in.
        setOffset((current) => clampOffset(
            { x: current.x * (value / zoom), y: current.y * (value / zoom) },
            value,
        ));
        setZoom(value);
    }, [clampOffset, zoom]);

    const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const limit = panLimitFor(zoom);
        if (limit.x === 0 && limit.y === 0) {
            return;
        }
        const rect = frameRef.current?.getBoundingClientRect();
        // A zero-sized rect means the frame hasn't been laid out yet.
        if (!rect || rect.width === 0 || rect.height === 0) {
            return;
        }
        if (typeof e.currentTarget.setPointerCapture === 'function') {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        dragRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            originX: offset.x,
            originY: offset.y,
            frameWidth: rect.width,
            frameHeight: rect.height,
        };
    }, [offset, panLimitFor, zoom]);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) {
            return;
        }
        // Pointer travel arrives in pixels; convert to frame percentages.
        setOffset(clampOffset({
            x: drag.originX + ((e.clientX - drag.startX) / drag.frameWidth) * 100,
            y: drag.originY + ((e.clientY - drag.startY) / drag.frameHeight) * 100,
        }, zoom));
    }, [clampOffset, zoom]);

    const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (dragRef.current?.pointerId === e.pointerId) {
            dragRef.current = null;
        }
    }, []);

    const { widthPct, heightPct } = useMemo(
        () => getFramePercentages(imageAspect ?? FRAME_ASPECT, zoom),
        [imageAspect, zoom],
    );

    const handleSave = useCallback(() => {
        if (saveInFlightRef.current) {
            return;
        }
        saveInFlightRef.current = true;
        const img = imageRef.current;
        // At zoom 1 nothing is cropped, so the original bytes go up untouched
        // — no re-encode, and a vector logo stays vector.
        if ((zoom === DEFAULT_ZOOM && offset.x === 0 && offset.y === 0) || !img || imageAspect === null) {
            onSave(file);
            return;
        }

        const outWidth = OUTPUT_WIDTH;
        const outHeight = Math.round(OUTPUT_WIDTH / FRAME_ASPECT);
        const canvas = document.createElement('canvas');
        canvas.width = outWidth;
        canvas.height = outHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            logger.logError('Unable to obtain a 2D canvas context while cropping the clinic logo.');
            onSave(file);
            return;
        }

        // Draw only — no background fill. The logo's own transparency has to
        // survive, which is exactly what the two destination previews show.
        const drawWidth = outWidth * (widthPct / 100);
        const drawHeight = outHeight * (heightPct / 100);
        ctx.drawImage(
            img,
            (outWidth - drawWidth) / 2 + (offset.x / 100) * outWidth,
            (outHeight - drawHeight) / 2 + (offset.y / 100) * outHeight,
            drawWidth,
            drawHeight,
        );

        // The crop only fills the frame when the zoomed logo overflows on both
        // axes; short of that it leaves transparent letterbox bands, which is
        // true even of a JPEG source.
        const cropIsOpaque = file.type === 'image/jpeg' && widthPct >= 100 && heightPct >= 100;
        // PNG everywhere transparency is possible. A JPEG that both starts
        // opaque and stays opaque is the one case where PNG buys nothing and
        // costs a lot — re-encoding a photographic JPEG as lossless PNG can
        // multiply its size, and a source that passed the 5MB input check
        // could land well over it.
        const outputType = cropIsOpaque ? 'image/jpeg' : 'image/png';
        const extension = cropIsOpaque ? 'jpg' : 'png';

        canvas.toBlob((blob) => {
            // Dismissed while the browser was encoding: the user cancelled, so
            // drop the result rather than uploading it behind their back.
            if (!isLiveRef.current) {
                return;
            }
            if (!blob) {
                logger.logError('Cropping the clinic logo produced an empty image.');
                onSave(file);
                return;
            }
            const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'clinic-logo';
            onSave(new File([blob], `${baseName}.${extension}`, { type: outputType }));
        }, outputType, cropIsOpaque ? JPEG_QUALITY : undefined);
    }, [zoom, offset, imageAspect, widthPct, heightPct, file, onSave]);

    const zoomFillPct = zoomToSlider(zoom) * 100;
    const panLimit = panLimitFor(zoom);
    const canPan = panLimit.x > 0 || panLimit.y > 0;
    const framedImageStyle = imageAspect === null
        ? { visibility: 'hidden' as const }
        // `left`/`top` percentages resolve against the frame, so one pair of
        // numbers positions the logo identically at any container size.
        : {
            width: `${widthPct}%`,
            height: `${heightPct}%`,
            left: `${50 + offset.x}%`,
            top: `${50 + offset.y}%`,
        };

    // The same crop, rendered at whatever size its container is.
    const renderFramedLogo = (testId: string) => (
        <div className="logo-preview-clip">
            {imageSrc &&
                <img
                    src={imageSrc}
                    alt={t('ClinicLogo')}
                    className="logo-preview-image"
                    draggable={false}
                    style={framedImageStyle}
                    data-testid={testId} />
            }
        </div>
    );

    return ReactDOM.createPortal(
        <div className="logo-preview-overlay" data-testid="logo-preview-overlay">
            <div
                className="logo-preview-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="logo-preview-title">
                <button
                    type="button"
                    className="logo-preview-close"
                    onClick={onClose}
                    aria-label={t('Cancel')}
                    data-testid="logo-preview-close">
                    <img src={closeIcon} alt="" />
                </button>

                <div className="logo-preview-header">
                    <h2 className="logo-preview-title" id="logo-preview-title">{t('ClinicLogo')}</h2>
                    <p className="logo-preview-subtitle">{t('LogoUsageHint')}</p>
                </div>

                <div className="logo-preview-content">
                    <div className="logo-preview-controls">
                        <div className="logo-preview-zoom">
                            <span className="logo-preview-zoom-icon" aria-hidden="true"><ZoomOutIcon /></span>
                            <input
                                type="range"
                                className="logo-preview-zoom-slider"
                                min={0}
                                max={1}
                                step={0.001}
                                value={zoomToSlider(zoom)}
                                aria-label={t('ZoomLogo')}
                                aria-valuetext={`${Math.round(zoom * 100)}%`}
                                onChange={(e) => applyZoom(sliderToZoom(Number(e.target.value)))}
                                style={{ '--zoom-fill': `${zoomFillPct}%` } as React.CSSProperties}
                                data-testid="logo-preview-zoom-slider" />
                            <span className="logo-preview-zoom-icon" aria-hidden="true"><ZoomInIcon /></span>
                        </div>
                        <div className="logo-preview-actions">
                            <button
                                type="button"
                                className="logo-preview-action"
                                onClick={onReplace}
                                aria-label={t('UploadNewLogo')}
                                data-testid="logo-preview-replace">
                                <EditIcon />
                            </button>
                            <button
                                type="button"
                                className="logo-preview-action"
                                onClick={onDelete}
                                aria-label={t('DeleteLogo')}
                                data-testid="logo-preview-delete">
                                <DeleteIcon />
                            </button>
                        </div>
                    </div>

                    <div
                        ref={frameRef}
                        className={`logo-preview-frame${canPan ? ' logo-preview-frame--pannable' : ''}`}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        data-testid="logo-preview-frame">
                        {imageSrc &&
                            <img
                                ref={imageRef}
                                src={imageSrc}
                                alt={t('ClinicLogo')}
                                className="logo-preview-image"
                                draggable={false}
                                onLoad={onImageLoad}
                                style={framedImageStyle}
                                data-testid="logo-preview-image" />
                        }
                    </div>

                    <div className="logo-preview-destinations">
                        <div className="logo-preview-destination">
                            <span className="logo-preview-destination-label">{t('PdfExports')}</span>
                            <div className="logo-preview-destination-box logo-preview-destination-box--light">
                                {renderFramedLogo('logo-preview-on-white')}
                            </div>
                        </div>
                        <div className="logo-preview-destination">
                            <span className="logo-preview-destination-label">{t('ScreenshotsBlackBackground')}</span>
                            <div className="logo-preview-destination-box logo-preview-destination-box--dark">
                                {renderFramedLogo('logo-preview-on-black')}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="logo-preview-footer">
                    <Button
                        backgroundColor="var(--gradient-primary-cta)"
                        color="white"
                        testId="logo-preview-save"
                        onClick={handleSave}
                        text={t('Save')}
                        width="100%"
                        height="48px"
                        borderRadius="24px"
                        fontSize="16px"
                        fontWeight="500" />
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default LogoPreviewDialog;
